import { useEffect, useState } from 'react';
import CardPreviewModal from './CardPreviewModal.jsx';
import { assetUrl } from '@/config/api';
import { useTranslation } from 'react-i18next';

const MAX_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);

const DeckImageEditor = ({ cards = [], selectedFiles = {}, onFilesSelected, busy = false, progress = '', uploadingCells = {} }) => {
  const { t } = useTranslation('dashboard');
  const [batchError, setBatchError] = useState('');
  const [fileErrorMap, setFileErrorMap] = useState({});
  const [previewOpen, setPreviewOpen] = useState(false);
  const [firstFileUrl, setFirstFileUrl] = useState('');
  useEffect(() => {
    const file = selectedFiles[1];
    if (!file) {
      setFirstFileUrl('');
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setFirstFileUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [selectedFiles[1]]);
  const uploaded = cards.filter((card) => Boolean(card.imageUrl)).length;
  const pending = Object.keys(selectedFiles).filter((number) =>
    !cards.some((card) => Number(card.cellNumber) === Number(number) && card.imageUrl)).length;
  const firstCard = cards.find((card) => Number(card.cellNumber) === 1);
  const firstCardPreview = firstFileUrl || firstCard?.imageUrl || '';

  const selectFiles = (entries) => {
    const valid = [];
    const errors = {};
    entries.forEach(([cellNumber, file]) => {
      if (!file) return;
      if (!ALLOWED_TYPES.has(file.type)) {
        errors[cellNumber] = t('imageDeck.invalidFormat');
      } else if (file.size > MAX_SIZE) {
        errors[cellNumber] = t('imageDeck.tooLarge');
      } else {
        valid.push([cellNumber, file]);
        errors[cellNumber] = '';
      }
    });
    setFileErrorMap((prev) => ({ ...prev, ...errors }));
    if (valid.length) onFilesSelected(valid);
  };

  const onBatchChange = (event) => {
    setBatchError('');
    const files = Array.from(event.target.files || []);
    const entries = [];
    const seen = new Set();
    const unmatched = [];
    files.forEach((file) => {
      const match = file.name.match(/^(?:card[-_ ]?)?0*(\d{1,2})\.(?:jpe?g|png|webp)$/i);
      const number = match ? Number(match[1]) : null;
      if (!number || number > 72 || seen.has(number)) {
        unmatched.push(file.name);
      } else {
        seen.add(number);
        entries.push([number, file]);
      }
    });
    if (unmatched.length) setBatchError(t('imageDeck.batchError', { count: unmatched.length }));
    selectFiles(entries);
    event.target.value = '';
  };

  return (
    <section className="image-deck-editor">
      <div className="image-deck-editor__intro">
        <strong>{t('imageDeck.imageCards')}</strong>
        <span>{t('imageDeck.uploadedCount', { count: uploaded + pending, total: 72 })}</span>
      </div>
      <p className="form-hint">{t('imageDeck.requirements')}</p>
      <div className="image-deck-editor__toolbar">
        <label className="image-deck-editor__batch">
          {t('imageDeck.batchUpload')}
          <input type="file" multiple accept=".jpg,.jpeg,.png,.webp" disabled={busy} onChange={onBatchChange} />
        </label>
        <button type="button" className="ghost-button" onClick={() => setPreviewOpen(true)}
          disabled={!firstCardPreview} aria-label={t('imageDeck.previewFirst')}>
          {t('imageDeck.previewFirst')}
        </button>
      </div>
      <p className="form-hint">{t('imageDeck.batchHint')}</p>
      {batchError && <p className="form-error">{batchError}</p>}
      {progress && <p className="form-hint" aria-live="polite">{progress}</p>}
      <div className="image-deck-editor__grid">
        {cards.map((card) => (
          <div key={card.cellNumber} className="image-deck-editor__item">
            <span className="image-deck-editor__number">{card.cellNumber}</span>
            {card.imageUrl ? (
              <img className="image-deck-editor__thumb" src={assetUrl(card.imageUrl)} alt={t('imageDeck.cardLabel', { number: card.cellNumber })} loading="lazy" />
            ) : (
              <div className="image-deck-editor__thumb image-deck-editor__placeholder" aria-hidden="true">＋</div>
            )}
            <label className="image-deck-editor__choose">
              {uploadingCells[card.cellNumber] ? t('imageDeck.uploading') : t('imageDeck.choose')}
              <input type="file" accept=".jpg,.jpeg,.png,.webp" disabled={busy || Boolean(uploadingCells[card.cellNumber])}
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) selectFiles([[card.cellNumber, file]]);
                  event.target.value = '';
                }} />
            </label>
            {selectedFiles[card.cellNumber] && <small title={selectedFiles[card.cellNumber].name} className="image-deck-editor__filename">{selectedFiles[card.cellNumber].name}</small>}
            {fileErrorMap[card.cellNumber] && <small className="form-error">{fileErrorMap[card.cellNumber]}</small>}
          </div>
        ))}
      </div>
      <CardPreviewModal
        open={previewOpen}
        card={firstCardPreview ? { cellNumber: 1, title: firstCard?.title || '1', imageUrl: firstCardPreview } : null}
        onClose={() => setPreviewOpen(false)}
        emptyMessage={t('imageDeck.previewMissing')}
        closeLabel={t('deckDetails.preview.close')}
      />
    </section>
  );
};

export default DeckImageEditor;
